import * as _ from 'lodash';
import { translateCountryCode } from '../../helpers/countryCodes';
import { ROLES } from '../../config/permissions/roles';
import { getEcoQuestionFieldNames } from '../../config/ecoQuestions';

export const EVALUATION_STATUS = {
  toBeDecidedByChairperson: 'TO_BE_DECIDED_BY_CHAIRPERSON',
  decidedByChairperson: 'DECIDED_BY_CHAIRPERSON',
  toBeDecided: 'TO_BE_DECIDED',
  readOnly: 'READ_ONLY',
  decided: 'DECIDED',
};

export const UPDATEABLE_ASSETS_FIELDS = ['projectPDF', 'url', 'video', 'bom'];

const CONCEPT_DISCIPLINE_NAME = 'Concepts';
export const isConceptDiscipline = (submission) => {
  return (
    submission?.disciplineName?.toLowerCase() ===
    CONCEPT_DISCIPLINE_NAME.toLowerCase()
  );
};

class Submission {
  constructor(
    submissionId,
    submissionName,
    submissionPrimaryMediaUrl,
    submissionPrimaryMediaThumbnailUrl,
    categoryName,
    disciplineName,
    submissionType,
    fields,
    maxPointsPerCriterion,
    pointsStep,
    points,
    comment,
    bookmarked,
    suggestedForNomination,
    discussInGroup,
    chairPersonMustDecide,
    scoresFromPriorSessions,
    nominated,
    evaluationStatus,
    ecoComment,
    ecoCommentPublished,
    nominatedVeto,
    discussed
  ) {
    this.submissionId = submissionId;
    this.submissionName = submissionName;
    this.submissionPrimaryMediaUrl = submissionPrimaryMediaUrl;
    this.submissionPrimaryMediaThumbnailUrl =
      submissionPrimaryMediaThumbnailUrl;
    this.categoryName = categoryName;
    this.disciplineName = disciplineName;
    this.submissionType = submissionType;
    this.fields = fields;
    this.maxPointsPerCriterion = maxPointsPerCriterion;
    this.pointsStep = pointsStep;
    this.points = points;
    this.comment = comment;
    this.bookmarked = bookmarked;
    this.suggestedForNomination = suggestedForNomination;
    this.discussInGroup = discussInGroup;
    this.chairPersonMustDecide = chairPersonMustDecide;
    this.scoresFromPriorSessions = scoresFromPriorSessions;
    this.nominated = nominated;
    this.evaluationStatus = evaluationStatus;
    this.ecoComment = ecoComment;
    this.ecoCommentPublished = ecoCommentPublished || false;
    this.nominatedVeto = nominatedVeto;
    this.discussed = discussed;
  }

  static from = (submission) => {
    if (!submission) return null;

    const ecoQuestionFieldNames = getEcoQuestionFieldNames();

    const fields = submission.fields?.reduce((prev, field) => {
      if (UPDATEABLE_ASSETS_FIELDS.includes(field.name)) {
        prev[field.name] = { value: field.value, updated: field.updated };
      } else if (ecoQuestionFieldNames.includes(field.name)) {
        prev[field.name] = {
          value: field.value,
          valueTranslated: field.valueTranslated,
        };
      } else if (field.name === 'subcategory') {
        prev[field.name] = field.valueTranslated;
      } else {
        prev[field.name] = field.value;
      }
      return prev;
    }, {});

    // Map impact fields to sustainability field, because of criterion change.
    // Dirty solution, because backend still provides impact field, instead of renaming it to sustainability.
    if (!fields.sustainability) {
      fields.sustainability = fields.impact;
    }

    return new Submission(
      submission.submissionId,
      submission.submissionName,
      submission.submissionPrimaryMediaUrl,
      submission.submissionPrimaryMediaThumbnailUrl,
      submission.categoryName,
      submission.disciplineName,
      submission.submissionType,
      fields,
      submission.maxPointsPerCriterion,
      submission.pointsStep,
      submission.points,
      submission.comment,
      submission.bookmarked,
      submission.suggestedForNomination,
      submission.discussInGroup,
      submission.chairPersonMustDecide,
      submission.scoresFromPriorSessions,
      submission.nominated,
      submission.evaluationStatus,
      submission.ecoComment,
      submission.ecoCommentPublished,
      submission.nominatedVeto,
      submission.discussed
    );
  };

  static of = (submissions) => {
    return submissions.map((submission) => Submission.from(submission));
  };

  static fetch = async (
    jurorGroupId,
    submissionId,
    impersonatingJurorId = null
  ) => {
    let url = `/if-api/jury-v2/jurorGroup/${jurorGroupId}/submission/${submissionId}/evaluation`;

    if (impersonatingJurorId) {
      url += `?jurorId=${impersonatingJurorId}`;
    }

    return fetch(url)
      .then((response) => response.json())
      .then((submission) =>
        Submission.from({ ...submission, ...submission.submission })
      )
      .catch((err) => console.error(err));
  };

  static updateEvaluation = async (
    jurorGroupId,
    submissionId,
    criterionId,
    points,
    impersonatingJurorId = null
  ) => {
    let url = `/if-api/jury-v2/jurorGroup/${jurorGroupId}/submission/${submissionId}/evaluation/criterion/${criterionId}`;

    if (impersonatingJurorId) {
      url += `?jurorId=${impersonatingJurorId}`;
    }

    return fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ points }),
    })
      .then((response) => response.ok)
      .catch((err) => console.error(err));
  };

  static deleteEvaluation = async (jurorGroupId, submissionId, criterionId) => {
    return fetch(
      `/if-api/jury-v2/jurorGroup/${jurorGroupId}/submission/${submissionId}/evaluation/criterion/${criterionId}`,
      {
        method: 'DELETE',
      }
    )
      .then((response) => response.ok)
      .catch((err) => console.error(err));
  };

  static setBookmark = async (value, jurorGroupId, submissionId) => {
    return fetch(
      `/if-api/jury-v2/jurorGroup/${jurorGroupId}/submission/${submissionId}/bookmark`,
      {
        method: value ? 'POST' : 'DELETE',
      }
    )
      .then((response) => response.ok)
      .catch((err) => console.error(err));
  };

  static setSuggestForNomination = async (
    value,
    jurorGroupId,
    submissionId,
    impersonatingJurorId = null
  ) => {
    let url = `/if-api/jury-v2/jurorGroup/${jurorGroupId}/submission/${submissionId}/suggestForNomination`;

    if (impersonatingJurorId) {
      url += `?jurorId=${impersonatingJurorId}`;
    }

    return fetch(url, {
      method: value ? 'POST' : 'DELETE',
    }).then((response) => response.json());
  };

  static setDiscussInGroup = async (value, jurorGroupId, submissionId) => {
    return fetch(
      `/if-api/jury-v2/jurorGroup/${jurorGroupId}/submission/${submissionId}/discussInGroup`,
      {
        method: value ? 'POST' : 'DELETE',
      }
    )
      .then((response) => response.ok)
      .catch((err) => console.error(err));
  };

  static setChairPersonMustDecide = async (
    value,
    jurorGroupId,
    submissionId
  ) => {
    return fetch(
      `/if-api/jury-v2/jurorGroup/${jurorGroupId}/submission/${submissionId}/chairPersonMustDecide`,
      {
        method: value ? 'POST' : 'DELETE',
      }
    )
      .then((response) => response.ok)
      .catch((err) => console.error(err));
  };

  static setComment = async (
    comment,
    jurorGroupId,
    submissionId,
    impersonatingJurorId = false
  ) => {
    let url = `/if-api/jury-v2/jurorGroup/${jurorGroupId}/submission/${submissionId}/comment`;

    if (impersonatingJurorId) {
      url += `?jurorId=${impersonatingJurorId}`;
    }

    return fetch(url, {
      method: 'POST',
      headers: new Headers({ 'content-type': 'text/plain' }),
      body: comment,
    })
      .then((response) => response.ok)
      .catch((err) => console.error(err));
  };

  static deleteComment = async (jurorGroupId, submissionId) => {
    return fetch(
      `/if-api/jury-v2/jurorGroup/${jurorGroupId}/submission/${submissionId}/comment`,
      {
        method: 'DELETE',
      }
    )
      .then((response) => response.ok)
      .catch((err) => console.error(err));
  };

  static setCommentsDiscussed = async ({ jurorGroupId, submissionId }) => {
    return fetch(
      `/if-api/jury-v2/jurorGroup/${jurorGroupId}/submission/${submissionId}/discussed`,
      {
        method: 'POST',
      }
    )
      .then((response) => response.ok)
      .catch((err) => console.error(err));
  };

  static setDiscussedInGroup = async (value, jurorGroupId, submissionId) => {
    return fetch(
      `/if-api/jury-v2/jurorGroup/${jurorGroupId}/submission/${submissionId}/discussedInGroup`,
      {
        method: value ? 'POST' : 'DELETE',
      }
    )
      .then((response) => response.ok)
      .catch((err) => console.error(err));
  };

  static setNominated = async (value, jurorGroupId, submissionId) => {
    return fetch(
      `/if-api/jury-v2/jurorGroup/${jurorGroupId}/submission/${submissionId}/nominated`,
      {
        method: value ? 'POST' : 'DELETE',
      }
    )
      .then((response) => response.ok)
      .catch((err) => {
        console.error(err);
        return false;
      });
  };

  static fetchJuryStatement = async (jurorGroupId, submissionId) => {
    return fetch(
      `/if-api/jury-v2/jurorGroup/${jurorGroupId}/submission/${submissionId}/juryStatement`
    )
      .then((response) => (response.ok ? response.text() : ''))
      .catch((err) => console.error(err));
  };

  static setJuryStatement = async ({
    juryStatement,
    jurorGroupId,
    submissionId,
  }) => {
    return fetch(
      `/if-api/jury-v2/jurorGroup/${jurorGroupId}/submission/${submissionId}/juryStatement`,
      {
        method: 'POST',
        headers: {
          'Content-type': 'text/plain',
        },
        body: juryStatement,
      }
    )
      .then((response) => response.ok)
      .catch((err) => console.error(err));
  };

  static deleteJuryStatement = async ({ jurorGroupId, submissionId }) => {
    return fetch(
      `/if-api/jury-v2/jurorGroup/${jurorGroupId}/submission/${submissionId}/juryStatement`,
      {
        method: 'DELETE',
      }
    )
      .then((response) => response.ok)
      .catch((err) => console.error(err));
  };

  static setEcoComment = async ({ jurorGroupId, submissionId, value }) => {
    return fetch(
      `/if-api/jury-v2/jurorGroup/${jurorGroupId}/submission/${submissionId}/ecoComment`,
      {
        method: 'POST',
        headers: {
          'Content-type': 'application/json',
        },
        body: JSON.stringify(value),
      }
    )
      .then((response) => response.ok)
      .catch((err) => {
        console.error(err);
        return false;
      });
  };

  static deleteEcoComment = async ({ jurorGroupId, submissionId }) => {
    return fetch(
      `/if-api/jury-v2/jurorGroup/${jurorGroupId}/submission/${submissionId}/ecoComment`,
      {
        method: 'DELETE',
      }
    )
      .then((response) => response.ok)
      .catch((err) => console.error(err));
  };

  static setNominatedVeto = async ({ jurorGroupId, submissionId, value }) => {
    return fetch(
      `/if-api/jury-v2/jurorGroup/${jurorGroupId}/submission/${submissionId}/nominatedVeto`,
      {
        method: value ? 'POST' : 'DELETE',
      }
    )
      .then((response) => response.ok)
      .catch((err) => console.error(err));
  };

  static getPoints = async (jurorGroupId, submissionId) => {
    return fetch(
      `/if-api/jury-v2/jurorGroup/${jurorGroupId}/submission/${submissionId}/points`,
      {
        method: 'GET',
      }
    )
      .then(async (response) => {
        // jurors points can be a null-filled array, which causes an error in the application.
        // set the null values to 0 as Michel Seuthe requested
        const json = await response.json();
        const jurors = json.jurors?.map((juror) => {
          return {
            ...juror,
            points: juror.points?.map((val) => val || { points: 0 }),
          };
        });
        return {
          ...json,
          jurors,
        };
      })
      .catch((err) => console.error(err));
  };

  static transferPointsFromPreselection = async (
    submission,
    jurorGroupId,
    impersonatingJurorId = null
  ) => {
    if (!submission.scoresFromPriorSessions.length) return;

    const scoresFromPriorSession = submission.scoresFromPriorSessions[0];

    const criteriaScores = scoresFromPriorSession.criteria.map((criterion) => ({
      id: criterion.id,
      score:
        Math.round(
          criterion.totalPoints /
            scoresFromPriorSession.numDecidedEvaluations /
            submission.pointsStep
        ) * submission.pointsStep,
    }));

    let results = [];

    for (const criterionScore of criteriaScores) {
      const res = await Submission.updateEvaluation(
        jurorGroupId,
        submission.submissionId,
        criterionScore.id,
        criterionScore.score,
        impersonatingJurorId
      );

      results.push(res);
    }

    results = results
      .map((isOk, index) => (isOk ? criteriaScores[index] : null))
      .filter((el) => !!el);

    return results;
  };

  static filter = (entries, filterKeys, juror, role) => {
    if (filterKeys.some((key) => key.includes('disciplineName'))) {
      const disciplineFilterName = filterKeys
        .find((key) => key.includes('disciplineName'))
        .split('__')[1];
      entries = entries.filter(
        (entry) => entry.disciplineName === disciplineFilterName
      );
    }
    if (filterKeys.includes('toBeDecided')) {
      entries = entries.filter((entry) => {
        const jurorGroupId = entry.evaluations.length
          ? entry.evaluations[0].jurorGroupId
          : 0;

        const role = juror.jurorGroups.find(
          (group) => group.jurorGroupId === jurorGroupId
        )?.role;

        if (role === ROLES.chairPerson) {
          return entry.evaluations.some(
            (evaluation) =>
              evaluation.status === EVALUATION_STATUS.toBeDecidedByChairperson
          );
        }

        if (role === ROLES.staff) {
          return entry.evaluations.some(
            (evaluation) =>
              evaluation.status === EVALUATION_STATUS.toBeDecided ||
              evaluation.status === EVALUATION_STATUS.toBeDecidedByChairperson
          );
        }

        const evaluation = entry.evaluations.find(
          (evaluation) =>
            evaluation.jurorId === entry?.jurorId ||
            evaluation.jurorId === juror?.jurorId
        );

        return (
          evaluation?.status === EVALUATION_STATUS.toBeDecided ||
          evaluation?.status === EVALUATION_STATUS.toBeDecidedByChairperson
        );
      });
    }
    if (filterKeys.some((key) => key.includes('jurorGroup'))) {
      const jurorGroupFilterName = filterKeys
        .find((key) => key.includes('jurorGroup'))
        .split('__')[1];
      entries = entries.filter((entry) => {
        return entry.evaluations.some(
          (evaluation) => evaluation.jurorGroupName === jurorGroupFilterName
        );
      });
    }
    if (filterKeys.some((key) => key.includes('presentationForm'))) {
      const presentationFormFilterValue = filterKeys
        .find((key) => key.includes('presentationForm'))
        .split('__')[1]
        .toLowerCase();
      entries = entries.filter((entry) => {
        return entry.extraFields.some(
          (field) =>
            field.name === 'presentationForm' &&
            field.value.toLowerCase() === presentationFormFilterValue
        );
      });
    }
    if (filterKeys.some((key) => key.includes('categoryName'))) {
      const categoryNameFilter = filterKeys
        .find((key) => key.includes('categoryName'))
        .split('__')[1];
      entries = entries.filter(
        (entry) => entry.categoryName === categoryNameFilter
      );
    }
    if (filterKeys.includes('status')) {
      entries = entries.filter((entry) => entry.status);
    }
    if (filterKeys.includes('suggestedForNomination')) {
      entries = entries.filter(
        (entry) =>
          entry.evaluations.some((e) => e.suggestedForNomination) ||
          entry.suggestedForNomination
      );
    }
    if (filterKeys.includes('nominated')) {
      entries = entries.filter((entry) => entry.nominated);
    }
    if (filterKeys.includes('chairPersonMustDecide')) {
      entries = entries.filter((entry) =>
        entry.evaluations.some(
          (evaluation) =>
            evaluation.status === EVALUATION_STATUS.toBeDecidedByChairperson ||
            evaluation.status === EVALUATION_STATUS.decidedByChairperson
        )
      );
    }
    if (filterKeys.includes('discussInGroup')) {
      entries = entries.filter(
        (entry) =>
          entry.numComments > 0 &&
          entry.evaluations.some(
            (evaluation) => evaluation.comment && !evaluation.discussed
          )
      );
    }
    if (filterKeys.includes('scoreAlert')) {
      entries = entries.filter(
        (entry) => entry.scoreAlertA || entry.scoreAlertB || entry.scoreAlertC
      );
    }
    if (filterKeys.includes('bookmarked')) {
      entries = entries.filter((entry) => entry.bookmarked);
    }
    if (filterKeys.some((key) => key.includes('country'))) {
      const countryFilterName = filterKeys
        .find((key) => key.includes('country'))
        .split('__')[1];
      entries = entries.filter(
        (entry) =>
          translateCountryCode(entry.accountCountryIsoCode) ===
          countryFilterName
      );
    }
    if (filterKeys.includes('hasZeroEvaluationCategory')) {
      entries = entries.filter((entry) =>
        entry?.evaluations?.some((evaluation) => evaluation?.hasZeroValues)
      );
    }
    if (filterKeys.includes('ecoComment_unpublished')) {
      entries = entries.filter(
        (entry) => entry.ecoComment && !entry.ecoCommentPublished
      );
    }
    if (filterKeys.includes('ecoComment')) {
      if (role === ROLES.staff) {
        entries = entries.filter((entry) => entry.ecoComment);
      } else {
        entries = entries.filter(
          (entry) => entry.ecoComment && entry.ecoCommentPublished
        );
      }
    }
    if (filterKeys.includes('nominatedVeto')) {
      entries = entries.filter((entry) => entry.nominatedVeto);
    }
    return entries;
  };

  static sort = (entries, { key, direction }) => {
    if (key === 'sort_averageScore') {
      return _.orderBy(entries, ['finalPoints'], direction);
    }

    if (key === 'sort_sustainabilityScore') {
      return _.orderBy(entries, ['sustainabilityScore'], direction);
    }

    if (key === 'sort_prevSessionSustainabilityScore') {
      return _.orderBy(
        entries,
        [
          (item) =>
            item?.scoresFromPriorSessions?.[0]?.criteria?.find(
              (criterion) => criterion.name === 'Sustainability'
            )?.totalPoints,
        ],
        direction
      );
    }

    return entries;
  };

  static search = (entries, search = '') => {
    search = search.toLowerCase();

    return entries.filter((submission) => {
      const submissionNameMatch = submission.submissionName
        .toLowerCase()
        .includes(search);

      const submissionCategoryMatch = submission.categoryName
        .toLowerCase()
        .includes(search);

      const disciplineNameMatch = submission?.disciplineName
        ?.toLowerCase()
        .includes(search);

      const submissionTypeMatch = submission.submissionType
        .toLowerCase()
        .includes(search);

      const submissionIdMatch = submission.submissionId
        .toString()
        .includes(search);

      return (
        submissionNameMatch ||
        submissionCategoryMatch ||
        disciplineNameMatch ||
        submissionTypeMatch ||
        submissionIdMatch
      );
    });
  };

  static getEvaluations = (submissionId) => {
    return fetch(`/if-api/jury-v2/submission/${submissionId}/evaluations`)
      .then((response) => (response.ok ? response.json() : ''))
      .catch((err) => console.error(err));
  };
}

export default Submission;
